import { API_BASE_URL, authFetch } from './api';


// === Types ===

export type ActivityType = 'like' | 'comment' | 'follow' | 'post' | 'share';
export type Platform = 'tiktok' | 'instagram' | 'facebook' | 'youtube' | 'reddit';

export type VerificationStatus =
  | 'pending'
  | 'verified_present'
  | 'verified_absent'
  | 'unknown'
  | 'skipped';

export type MentionState = 'matched' | 'unmatched' | 'any';

export interface AgentActivity {
  id: string;
  session_id: string;
  agent_id: string;
  pad_id: string;
  platform: string;
  activity_type: string;
  target_username: string | null;
  target_content_url: string | null;
  content_text: string | null;
  brand_id: string | null;
  brand_alias_matched: string | null;
  typed_text: string | null;
  verification_status: VerificationStatus;
  verification_notes: string | null;
  step_number: number;
  result: string;
  error_message: string | null;
  session_started_at: string | null;
  session_completed_at: string | null;
  created_at: string | null;
}

export interface ActivityFilters {
  platform?: string;
  activity_type?: string;
  pad_id?: string;
  session_id?: string;
  brand_id?: string;                    // UUID, or "none" for non-branded only
  verification_status?: VerificationStatus;
  mention_state?: MentionState;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

export interface ListActivitiesResponse {
  activities: AgentActivity[];
  total: number;
  limit: number;
  offset: number;
}

export interface ActivityStatsResponse {
  total: number;
  by_activity_type: Record<string, number>;
  by_platform: Record<string, number>;
  by_platform_activity: Record<string, Record<string, number>>;
  by_brand: Record<string, number>;
  by_verification_status: Record<string, number>;
  success_count: number;
  failed_count: number;
  false_positive_signals: {
    verified_absent_count: number;
    branded_comments_total: number;
    branded_comments_unmatched: number;
  };
}

export interface SessionActivitiesResponse {
  session_id: string;
  total: number;
  by_pad_id: Record<string, AgentActivity[]>;
}

export interface ActivityDevice {
  pad_id: string;
  device_name: string;
  device_group: string | null;
  platform: string;
  username: string | null;
}

// === API Functions ===

const BASE = `${API_BASE_URL}/vmos-activities`;

function buildQuery(filters: Record<string, unknown>): string {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') {
      params.append(k, String(v));
    }
  });
  const q = params.toString();
  return q ? `?${q}` : '';
}

export async function listActivities(filters: ActivityFilters = {}): Promise<ListActivitiesResponse> {
  const res = await authFetch(`${BASE}${buildQuery(filters as Record<string, unknown>)}`);
  return res.json();
}

export async function getActivityStats(filters: Omit<ActivityFilters, 'limit' | 'offset' | 'activity_type'> = {}): Promise<ActivityStatsResponse> {
  const res = await authFetch(`${BASE}/stats${buildQuery(filters as Record<string, unknown>)}`);
  return res.json();
}

export async function getSessionActivities(sessionId: string): Promise<SessionActivitiesResponse> {
  const res = await authFetch(`${BASE}/sessions/${sessionId}`);
  return res.json();
}

export async function listActivityDevices(): Promise<{ devices: ActivityDevice[] }> {
  const res = await authFetch(`${BASE}/devices`);
  return res.json();
}
